import React, { useEffect, useRef, useState } from "react";
import {
  MapPin,
  Phone,
  Envelope,
  Clock,
  ArrowRight,
  Train,
  CarProfile,
  AirplaneTilt,
  X,
  MagnifyingGlass,
  CircleNotch,
} from "@phosphor-icons/react";

// Icona "P" stilizzata per il tile parcheggio (Phosphor non ha un icon Parking).
function ParkingLetter() {
  return (
    <span className="font-serif font-semibold text-xl leading-none">P</span>
  );
}
import Reveal from "./Reveal";
import EsriMap from "./EsriMap";
import { useLang } from "../context/LanguageContext";

const ARCGIS_KEY = process.env.REACT_APP_ARCGIS_API_KEY;
const GEOCODE_BASE = "https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer";
const ROUTE_URL = "https://route-api.arcgis.com/arcgis/rest/services/World/Route/NAServer/Route_World/solve";

export default function ContactSection({ settings }) {
  const { t, lang } = useLang();
  if (!settings) return null;

  const reach = t.contact.howToReach;
  const transport = [
    { icon: Train, ...reach.train },
    { icon: CarProfile, ...reach.car },
    { icon: AirplaneTilt, ...reach.plane },
    { icon: ParkingLetter, ...reach.parking },
  ];

  return (
    <section className="relative py-24 lg:py-40 bg-[#09090b]" data-testid="contact-section">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overline mb-4">{t.contact.overline}</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1]">
                {t.contact.title.split("\n").map((l, i) => (<span key={l || `contact-line-${i}`} className="block">{l}</span>))}
              </h2>
              <p className="mt-6 text-white/70 max-w-md">{t.contact.subtitle}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12 space-y-6">
                <InfoRow icon={<MapPin size={18} />} label={t.contact.address} value={settings.address} />
                <InfoRow icon={<Phone size={18} />} label={t.contact.phone} value={settings.phone} href={`tel:${(settings.phone || "").replace(/\s+/g, "")}`} />
                <InfoRow icon={<Envelope size={18} />} label={t.contact.email} value={settings.email} href={`mailto:${settings.email || ""}`} />
                <InfoRow icon={<Clock size={18} />} label={t.contact.hours} value="24/7" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <MapAndDirections settings={settings} reach={reach} lang={lang} />
            </Reveal>
          </div>
        </div>

        {/* Come raggiungerci — transport tiles */}
        <Reveal delay={0.2}>
          <div className="mt-16 lg:mt-24" data-testid="how-to-reach">
            <div className="overline mb-6 text-[#C5A059]">{reach.title}</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {transport.map(({ icon: Icon, label, sub }) => (
                <div
                  key={label}
                  className="bg-[#0c0c0e] p-6 lg:p-8 flex items-start gap-4"
                >
                  <div className="w-11 h-11 rounded-full border border-[#C5A059]/40 flex items-center justify-center text-[#C5A059] shrink-0">
                    {typeof Icon === "function" && Icon.length === 0 ? <Icon /> : <Icon size={20} weight="light" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif text-lg lg:text-xl leading-tight">{label}</div>
                    <div className="text-white/55 text-sm mt-1 leading-relaxed">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MapAndDirections({ settings, reach, lang }) {
  const [mode, setMode] = useState("idle"); // idle | search | route
  const [origin, setOrigin] = useState(null); // {lat, lng}
  const [routePath, setRoutePath] = useState(null); // [[lat,lng], ...]
  const [routeInfo, setRouteInfo] = useState(null); // {km, min}

  function reset() {
    setMode("idle");
    setOrigin(null);
    setRoutePath(null);
    setRouteInfo(null);
  }

  function onRouteComputed({ originPoint, path, km, min }) {
    setOrigin(originPoint);
    setRoutePath(path);
    setRouteInfo({ km, min });
    setMode("route");
  }

  // OpenStreetMap fallback (design-system rule: no Google Maps).
  const osmFallback = `https://www.openstreetmap.org/directions?from=&to=${settings.map_lat}%2C${settings.map_lng}&zoom=15`;
  const hasKey = Boolean(ARCGIS_KEY);

  return (
    <>
      <div className="relative aspect-[4/3] lg:aspect-[5/4] w-full overflow-hidden border border-white/10" data-testid="map-frame">
        <EsriMap
          lat={settings.map_lat}
          lng={settings.map_lng}
          label={settings.address}
          origin={origin}
          routePath={routePath}
        />
      </div>

      {!hasKey ? (
        <a
          href={osmFallback}
          target="_blank"
          rel="noreferrer"
          data-testid="directions-link"
          className="mt-4 inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090b]"
        >
          {reach.openDirections}
          <ArrowRight size={14} weight="bold" />
        </a>
      ) : mode === "idle" ? (
        <button
          type="button"
          onClick={() => setMode("search")}
          data-testid="directions-open"
          className="mt-4 inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090b]"
        >
          {reach.openDirections}
          <ArrowRight size={14} weight="bold" />
        </button>
      ) : mode === "search" ? (
        <DirectionsSearch
          reach={reach}
          lang={lang}
          destination={{ lat: settings.map_lat, lng: settings.map_lng }}
          onClose={reset}
          onRouteComputed={onRouteComputed}
        />
      ) : (
        <DirectionsResult reach={reach} info={routeInfo} onClose={reset} onChange={() => { setOrigin(null); setRoutePath(null); setRouteInfo(null); setMode("search"); }} />
      )}
    </>
  );
}

function DirectionsSearch({ reach, lang, destination, onClose, onRouteComputed }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (query.trim().length < 3) { setSuggestions([]); return; }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const url = `${GEOCODE_BASE}/suggest?f=json&text=${encodeURIComponent(query)}&countryCode=ITA&location=${destination.lng},${destination.lat}&maxSuggestions=6&token=${ARCGIS_KEY}`;
        const res = await fetch(url, { signal: ctrl.signal });
        const data = await res.json();
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        setSuggestOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setSuggestions([]);
      }
    }, 250);
    return () => { ctrl.abort(); clearTimeout(timer); };
  }, [query, destination.lat, destination.lng]);

  async function pickSuggestion(s) {
    setQuery(s.text);
    setSuggestions([]);
    setSuggestOpen(false);
    setLoading(true);
    setError(null);
    try {
      const geoUrl = `${GEOCODE_BASE}/findAddressCandidates?f=json&magicKey=${encodeURIComponent(s.magicKey)}&outFields=Match_addr&outSR=4326&token=${ARCGIS_KEY}`;
      const geoRes = await fetch(geoUrl);
      const geoData = await geoRes.json();
      const c = geoData.candidates?.[0];
      if (!c) throw new Error("address_not_found");
      const originPoint = { lat: c.location.y, lng: c.location.x };

      const stops = `${originPoint.lng},${originPoint.lat};${destination.lng},${destination.lat}`;
      const routeUrl = `${ROUTE_URL}?f=json&stops=${encodeURIComponent(stops)}&returnRoutes=true&returnGeometry=true&outSR=4326&directionsLanguage=${lang === "en" ? "en" : "it"}&token=${ARCGIS_KEY}`;
      const routeRes = await fetch(routeUrl);
      const routeData = await routeRes.json();
      const feat = routeData.routes?.features?.[0];
      if (!feat?.geometry?.paths?.[0]) throw new Error("route_failed");
      const path = feat.geometry.paths[0].map(([x, y]) => [y, x]);
      const km = Number(feat.attributes?.Total_Kilometers ?? 0);
      const min = Number(feat.attributes?.Total_TravelTime ?? 0);
      onRouteComputed({ originPoint, path, km, min });
    } catch (e) {
      setError(reach.directionsError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="mt-4 relative bg-[#0c0c0e] border border-white/10 p-4 sm:p-5"
      data-testid="directions-search"
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0 bg-[#09090b] border border-white/10 px-4 py-3">
          <MagnifyingGlass size={18} className="text-[#C5A059] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setError(null); }}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => setTimeout(() => setSuggestOpen(false), 180)}
            placeholder={reach.directionsPrompt}
            data-testid="directions-input"
            className="flex-1 min-w-0 bg-transparent outline-none text-white placeholder:text-white/40 text-sm"
            disabled={loading}
          />
          {loading && <CircleNotch size={18} className="text-[#C5A059] animate-spin" />}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={reach.directionsClose}
          data-testid="directions-close"
          className="shrink-0 w-11 h-11 border border-white/10 text-white/70 hover:text-[#C5A059] hover:border-[#C5A059]/50 transition-colors flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090b]"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {suggestOpen && suggestions.length > 0 && (
        <ul
          data-testid="directions-suggestions"
          className="absolute left-4 right-4 sm:left-5 sm:right-5 top-[4.5rem] z-20 bg-[#09090b] border border-white/10 max-h-64 overflow-y-auto"
        >
          {suggestions.map((s) => (
            <li key={s.magicKey}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                className="w-full text-left px-4 py-3 text-sm text-white/85 hover:bg-[#C5A059] hover:text-black transition-colors border-b border-white/5 last:border-b-0"
              >
                {s.text}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div className="mt-3 text-xs text-red-400/90" role="alert" data-testid="directions-error">
          {error}
        </div>
      )}
    </div>
  );
}

function DirectionsResult({ reach, info, onClose, onChange }) {
  const kmLabel = `${info.km.toFixed(1)} km`;
  const minLabel = `${Math.round(info.min)} min`;
  return (
    <div
      className="mt-4 bg-[#0c0c0e] border border-white/10 p-4 sm:p-5 flex items-center gap-4"
      data-testid="directions-result"
    >
      <div className="flex-1 min-w-0">
        <div className="overline text-[#C5A059] text-[0.65rem]">{reach.directionsResult}</div>
        <div className="mt-1 font-serif text-2xl leading-tight">
          {kmLabel}
          <span className="text-white/40 mx-3">·</span>
          {minLabel}
        </div>
      </div>
      <button
        type="button"
        onClick={onChange}
        data-testid="directions-change"
        className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[#C5A059] transition-colors px-3 py-2 border border-white/10 hover:border-[#C5A059]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090b]"
      >
        {reach.directionsChange}
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label={reach.directionsClose}
        data-testid="directions-close"
        className="shrink-0 w-11 h-11 border border-white/10 text-white/70 hover:text-[#C5A059] hover:border-[#C5A059]/50 transition-colors flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090b]"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}

function InfoRow({ icon, label, value, href }) {
  const valueEl = href ? (
    <a
      href={href}
      className="text-white/90 hover:text-[#C5A059] transition-colors focus-visible:outline-none focus-visible:text-[#C5A059]"
    >
      {value}
    </a>
  ) : (
    <span className="text-white/90">{value}</span>
  );
  return (
    <div className="flex items-start gap-4 pb-6 border-b border-white/10">
      <div className="w-10 h-10 rounded-full border border-[#C5A059]/40 flex items-center justify-center text-[#C5A059] shrink-0">
        {icon}
      </div>
      <div>
        <div className="overline text-white/50 mb-1">{label}</div>
        {valueEl}
      </div>
    </div>
  );
}
